import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, History, RotateCcw, Mail, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { listOfferEmailTemplateVersions } from '../api';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const RateBadge = ({ rate, sentCount }) => {
  if (!sentCount) {
    return <span className="text-xs text-slate-400 flex items-center gap-1"><Minus size={12} /> No sends yet</span>;
  }
  const Icon = rate >= 50 ? TrendingUp : TrendingDown;
  const color = rate >= 70 ? 'text-emerald-600 bg-emerald-50' : rate >= 40 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${color}`}>
      <Icon size={11} /> {rate}% accepted
    </span>
  );
};

export default function EmailVersionHistoryDialog({ open, onOpenChange, templateId, templateName, onRestore }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ versions: [], summary: {} });

  const fetchVersions = async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const res = await listOfferEmailTemplateVersions(templateId);
      setData(res.data || { versions: [], summary: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load email version history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  const handleRestore = (v) => {
    onRestore?.({ email_subject: v.email_subject, email_body: v.email_body });
    toast.success(`Restored Version ${v.version_number} into the editor. Click "Update Template" to save.`);
    onOpenChange(false);
  };

  const { versions = [], summary = {} } = data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="email-version-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} className="text-indigo-600" />
            Email Version History {templateName ? `— ${templateName}` : ''}
          </DialogTitle>
          <DialogDescription>
            Every time you save a different email subject or body, a new version is archived. Compare
            acceptance rates to find the copy that converts best.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading version history…
          </div>
        ) : versions.length === 0 ? (
          <div className="py-10 text-center text-slate-500" data-testid="empty-versions">
            <Mail size={40} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No email versions yet. Save an email subject or body to start tracking history.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 py-3 mb-2 border-b">
              <div className="text-center">
                <div className="text-xs text-slate-500">Total Versions</div>
                <div className="text-xl font-bold text-slate-800" data-testid="summary-total-versions">{summary.total_versions ?? 0}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500">Total Sent</div>
                <div className="text-xl font-bold text-slate-800" data-testid="summary-total-sent">{summary.total_sent ?? 0}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500">Overall Acceptance</div>
                <div className="text-xl font-bold text-indigo-600" data-testid="summary-acceptance-rate">
                  {summary.overall_acceptance_rate ?? 0}%
                </div>
              </div>
            </div>

            <div className="space-y-3" data-testid="version-list">
              {versions.map((v, idx) => (
                <div
                  key={v.id}
                  className={`border rounded-lg p-3 ${idx === 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200'}`}
                  data-testid={`version-item-${v.version_number}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${idx === 0 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                        v{v.version_number}{idx === 0 && ' • CURRENT'}
                      </span>
                      <span className="text-xs text-slate-500">{formatDate(v.created_at)}</span>
                      <span className="text-xs text-slate-500">by {v.created_by || 'system'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <RateBadge rate={v.acceptance_rate} sentCount={v.sent_count} />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestore(v)}
                        data-testid={`restore-v${v.version_number}`}
                      >
                        <RotateCcw size={12} className="mr-1" /> Restore
                      </Button>
                    </div>
                  </div>

                  <div className="text-xs space-y-1">
                    <div>
                      <span className="text-slate-500 font-mono">Subject: </span>
                      <span className="font-medium text-slate-800">{v.email_subject || <em className="text-slate-400">(empty)</em>}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 font-mono">Body: </span>
                      <code className="text-slate-700 bg-slate-50 px-1 py-0.5 rounded text-[11px]">
                        {(v.email_body || '').slice(0, 140)}{(v.email_body || '').length > 140 ? '…' : ''}
                      </code>
                    </div>
                    <div className="flex items-center gap-3 pt-1 text-[11px] text-slate-500">
                      <span>📤 Sent: <strong className="text-slate-700">{v.sent_count}</strong></span>
                      <span>✅ Accepted: <strong className="text-emerald-700">{v.accepted_count}</strong></span>
                      <span>❌ Rejected: <strong className="text-rose-700">{v.rejected_count}</strong></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
